import { css } from '@emotion/react'
import styled from '@emotion/styled'

export const mobile = '@media (max-width: 767px)'
const font = '"Segoe UI", Arial'
const colorGreen = '#78be20'
const colorBorder = '#eeeef0'
const colorNoticeBg = '#eef7e4'
const colorAlertBg = '#fadfdd'
const colorAlertTitle = '#da291c'

const flexRow = css`
  display: flex;
`
const flexCol = css`
  ${flexRow};
  flex-direction: column;
`

const cardBase = css`
  border: 2px solid ${colorBorder};
  padding: 30px;
  border-radius: 20px;
  margin: 0;
`

const responsiveColumn = (width: string) => css`
  ${cardBase};
  width: ${width};

  ${mobile} {
    width: 100%;
  }
`

export const Container = styled.section`
  max-width: 1180px;
  margin: 0 auto;
  padding: 80px 15px 0;
  position: relative;

  ${mobile} {
    padding-top: 10px;
  }

  ul {
    list-style: disc outside;
    padding-left: 1.5em;
  }
`

export const Wrapper = styled.div``

export const Title = styled.h2`
  font: 400 35px ${font};
  color: ${colorGreen};
  ${flexRow};
  margin-bottom: 20px;

  span {
    font-weight: 800;
    margin-right: 10px;
  }

  ${mobile} {
    font-size: 28px;
  }
`

export const Content = styled.div`
  ${flexRow};
  gap: 25px;

  h3 {
    font-size: 22px;
    line-height: 28px;
    font-weight: 600;
    margin: 0;
  }

  p {
    font-size: 18px;
    line-height: 26px;
    font-weight: 400;
    margin: 10px 0 0;
  }

  ${mobile} {
    flex-direction: column;
  }
`

export const Step1 = styled.div`
  font-family: ${font};
  ${responsiveColumn('700px')};
  font-size: 28px;
  ${flexRow};
  gap: 20px;

  svg {
    width: 310px;
    height: 300px;
  }

  ${mobile} {
    flex-direction: column;
    svg {
      width: 100%;
      height: auto;
    }
  }
`

export const Step2 = styled.div`
  font-family: ${font};
  ${responsiveColumn('100%')};
  font-size: 28px;
  ${flexRow};
  gap: 30px;

  svg {
    width: 430px;
    height: 300px;
  }

  ${mobile} {
    flex-direction: column;
    svg {
      width: 100%;
      height: auto;
    }
  }
`

export const Step3 = styled.div`
  font-family: ${font};
  width: 100%;
  margin-top: 40px;
  font-size: 28px;
  ${flexCol};

  p {
    font-size: 22px;
    margin-top: 10px;
  }
`

export const Bullet = styled.div`
  background: ${colorGreen};
  width: 50px;
  height: 50px;
  border-radius: 50%;
  ${flexRow};
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  margin-bottom: 10px;
`

export const InnerTop = styled.div`
  margin-top: 70px;
`

export const InnerTopLink = styled.strong`
  color: ${colorGreen};
  font-size: 16px;
`

export const RowTop = styled.div`
  ${flexRow};
  gap: 15px;
  margin-top: 50px;

  ${mobile} {
    flex-direction: column;
  }
`

export const ColumnTop1 = styled.div`
  ${responsiveColumn('25%')};
  font-size: 30px;
  font-weight: 800;

  svg {
    margin-bottom: 20px;
  }
`

export const ColumnTop2 = styled.div`
  ${responsiveColumn('35%')}
`
export const ColumnTop3 = styled.div`
  ${responsiveColumn('60%')}
`
export const ColumnTop4 = styled.div`
  ${responsiveColumn('40%')}
`
export const ColumnTop5 = styled.div`
  ${responsiveColumn('40%')}
`

export const ColumnTop6 = styled.div`
  ${responsiveColumn('20%')};
  font-size: 30px;
  font-weight: 800;

  svg {
    margin-bottom: 20px;
  }
`

export const ColumnTop7 = styled.div`
  ${responsiveColumn('50%')}
`

export const RowTop2 = styled.div`
  ${flexRow};
  justify-content: flex-end;
  margin-bottom: 20px;
`

export const Link = styled.div`
  color: ${colorGreen};
  font-weight: 800;
`

export const SubTitle = styled.div`
  font-weight: 800;
  font-size: 22px;
  margin: 35px 0 25px;
`

export const HighLights = styled.div`
  ${flexRow};
  justify-content: space-between;
  gap: 45px;
  margin-bottom: 40px;

  ${mobile} {
    flex-direction: column;
  }
`

export const Item = styled.div`
  font-size: 20px;
  ${flexCol};

  ${mobile} {
    align-items: center;
    text-align: center;
  }
`

export const Circle = styled.div`
  ${flexRow};
  align-items: center;
  justify-content: center;
  width: 85px;
  height: 85px;
  border-radius: 50%;
  border: 2px solid ${colorBorder};
  margin-bottom: 20px;
`

export const Notice = styled.div`
  border-radius: 20px;
  background: ${colorNoticeBg};
  padding: 20px 30px;

  h3 {
    font-size: 20px;
    font-weight: 700;
  }
  p {
    margin-top: 10px;
    font-size: 20px;
    line-height: 30px;
  }
`

export const RowDouble = styled.div`
  ${flexRow};
  gap: 15px;
  margin-top: 15px;
  ${mobile} {
    flex-direction: column;
  }
`

export const RowItem = styled.div`
  ${responsiveColumn('55%')};
  font-size: 18px;
`

export const RowItemAlert = styled.div`
  ${responsiveColumn('45%')};
  ${flexRow};
  gap: 20px;
  background: ${colorAlertBg};

  p {
    font-size: 18px;
    line-height: 28px;
  }
  svg {
    width: 400px;
  }

  ${mobile} {
    flex-direction: column;
    align-items: flex-start;
    svg {
      width: 100%;
      height: auto;
    }
  }
`

export const Number = styled.div`
  color: ${colorGreen};
  font-weight: 800;
  font-size: 25px;
`

export const RowText = styled.div`
  ${flexRow};
  gap: 40px;
  justify-content: space-between;
  margin-bottom: 30px;
  ${mobile} {
    flex-direction: column;
  }
`

export const ColumnText = styled.div`
  p {
    font-size: 20px;
    line-height: 30px;
    margin-bottom: 10px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`

export const ColumnTextAlert = styled.div`
  ${responsiveColumn('990px')};
  ${flexRow};
  gap: 20px;
  align-items: center;
  background: ${colorAlertBg};

  h3 {
    color: ${colorAlertTitle};
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 4px;
  }

  p {
    font-size: 17px;
    line-height: 28px;
  }

  svg {
    width: 200px;
    height: 90px;
  }

  ${mobile} {
    flex-direction: column;
    svg {
      width: 100%;
      height: auto;
    }
  }
`

export const RowTextBottom = styled.div`
  margin-bottom: 50px;

  h3 {
    font-size: 20px;
    line-height: 30px;
    font-weight: 700;
    margin: 20px 0 10px;
  }

  p {
    font-size: 17px;
    line-height: 27px;
  }
`
